/**
 * Mock Data for Demo Mode
 *
 * This file contains realistic sample data that allows users to experience
 * the full functionality of Canvas Companion without connecting their real
 * Canvas account. This reduces friction and helps users understand the
 * value proposition before committing to the API token setup.
 */

// Demo user profile
export const MOCK_USER = {
  id: 131973,
  name: 'Demo Student',
  short_name: 'Demo',
  sortable_name: 'Student, Demo',
  email: 'demo.student@school.edu',
  avatar_url: null,
  locale: 'en',
};

// Helper to generate dates relative to today
const daysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

const daysAgo = (days) => daysFromNow(-days);

// Current term info
const CURRENT_TERM = {
  id: 201,
  name: '2025/2026 - S1',
  start_at: '2025-08-04T04:00:00Z',
  end_at: '2025-12-20T04:59:00Z',
  workflow_state: 'active',
  grading_period_group_id: null,
  created_at: '2025-07-08T05:36:41Z',
};

// Mock courses following real Canvas API structure
export const MOCK_COURSES = [
  {
    id: '461001',
    api_course_id: '461001',
    name: 'P2-AP Calculus BC-JOHNSON',
    course_code: 'P2-AP Calculus BC-JOHNSON',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'modules',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'abc123XYZ789calcBC001',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_abc123XYZ789calcBC001.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: 'A',
        computed_current_score: 94.2,
        computed_current_letter_grade: 'A',
        computed_final_grade: 'A',
        computed_final_score: 93.8,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
  {
    id: '461002',
    api_course_id: '461002',
    name: 'P3-AP Physics C Mechanics-CHEN',
    course_code: 'P3-AP Physics C Mechanics-CHEN',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'modules',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'def456ABC123physics002',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_def456ABC123physics002.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: 'B+',
        computed_current_score: 87.8,
        computed_current_letter_grade: 'B+',
        computed_final_grade: 'B+',
        computed_final_score: 86.2,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
  {
    id: '461003',
    api_course_id: '461003',
    name: 'P4-AP English Lit-MARTINEZ',
    course_code: 'P4-AP English Lit-MARTINEZ',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'assignments',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'ghi789DEF456english003',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_ghi789DEF456english003.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: 'A-',
        computed_current_score: 91.5,
        computed_current_letter_grade: 'A-',
        computed_final_grade: 'A-',
        computed_final_score: 90.8,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
  {
    id: '461004',
    api_course_id: '461004',
    name: 'P5-AP US History-WILLIAMS',
    course_code: 'P5-AP US History-WILLIAMS',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'wiki',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'jkl012GHI789history004',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_jkl012GHI789history004.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: 'B+',
        computed_current_score: 89.3,
        computed_current_letter_grade: 'B+',
        computed_final_grade: 'B+',
        computed_final_score: 88.1,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
  {
    id: '461005',
    api_course_id: '461005',
    name: 'P6-Spanish III-GARCIA',
    course_code: 'P6-Spanish III-GARCIA',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'feed',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'mno345JKL012spanish005',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_mno345JKL012spanish005.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: 'A',
        computed_current_score: 96.1,
        computed_current_letter_grade: 'A',
        computed_final_grade: 'A',
        computed_final_score: 95.4,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
  {
    id: '461006',
    api_course_id: '461006',
    name: 'P8-AP Comp Sci Prin-THOMPSON',
    course_code: 'P8-AP Comp Sci Prin-THOMPSON',
    account_id: 157,
    created_at: '2025-07-08T05:45:13Z',
    start_at: null,
    default_view: 'wiki',
    enrollment_term_id: 201,
    is_public: false,
    grading_standard_id: null,
    root_account_id: 1,
    uuid: 'pqr678MNO345compsci006',
    license: 'private',
    grade_passback_setting: 'nightly_sync',
    end_at: null,
    public_syllabus: false,
    public_syllabus_to_auth: false,
    storage_quota_mb: 5242,
    is_public_to_auth_users: false,
    homeroom_course: false,
    course_color: null,
    friendly_name: null,
    term: CURRENT_TERM,
    apply_assignment_group_weights: true,
    calendar: { ics: 'https://school.instructure.com/feeds/calendars/course_pqr678MNO345compsci006.ics' },
    time_zone: 'America/New_York',
    image_download_url: null,
    concluded: false,
    blueprint: false,
    template: false,
    enrollments: [
      {
        type: 'student',
        role: 'StudentEnrollment',
        role_id: 3,
        user_id: 131973,
        enrollment_state: 'active',
        limit_privileges_to_course_section: false,
        current_grading_period_id: null,
        current_grading_period_title: null,
        has_grading_periods: false,
        multiple_grading_periods_enabled: false,
        computed_current_grade: null,
        computed_current_score: 63.9,
        computed_current_letter_grade: null,
        computed_final_grade: null,
        computed_final_score: 60.49,
        totals_for_all_grading_periods_option: false,
        current_period_computed_current_score: null,
        current_period_computed_final_score: null,
        current_period_computed_current_grade: null,
        current_period_computed_final_grade: null,
      }
    ],
    hide_final_grades: false,
    workflow_state: 'available',
    restrict_enrollments_to_course_dates: false,
    has_grading_periods: false,
    multiple_grading_periods_enabled: false,
    has_weighted_grading_periods: false,
    access_restricted: false,
  },
];

// Detailed course data including assignments (following Canvas API structure)
export const MOCK_COURSE_DETAILS = {
  461001: {
    id: '461001',
    name: 'P2-AP Calculus BC-JOHNSON',
    course_code: 'P2-AP Calculus BC-JOHNSON',
    overall_grade: 94.2,
    assignments: [
      {
        id: 1001,
        name: 'Integration Techniques Quiz',
        description: '<p>Quiz covering u-substitution, integration by parts, and partial fractions. Show all work for full credit.</p>',
        due_at: daysAgo(14),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 100001,
        group_name: 'Quizzes',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(14),
        score: 48,
        grade: '48',
        graded_at: daysAgo(12),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 1002,
        name: 'Differential Equations Problem Set',
        description: '<p>Solve differential equations using separation of variables and integrating factors. Problems 1-15 from Chapter 7.</p>',
        due_at: daysAgo(7),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 100002,
        group_name: 'Homework',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(8),
        score: 95,
        grade: '95',
        graded_at: daysAgo(5),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 1003,
        name: 'Unit 3 Exam: Applications of Integration',
        description: '<p>Comprehensive exam on area, volume, and arc length applications. Calculator allowed for Section B only.</p>',
        due_at: daysAgo(3),
        points_possible: 150,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 100003,
        group_name: 'Exams',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(3),
        score: 142,
        grade: '142',
        graded_at: daysAgo(1),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 1004,
        name: 'Series Convergence Practice',
        description: '<p>Practice problems on convergence tests: ratio, root, comparison, and integral tests. Complete all odd-numbered problems.</p>',
        due_at: daysFromNow(3),
        points_possible: 75,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 100002,
        group_name: 'Homework',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 1005,
        name: 'Parametric Equations Lab',
        description: '<p>Graphing calculator lab exploring parametric curves and their derivatives. Submit screenshots and analysis.</p>',
        due_at: daysAgo(2),
        points_possible: 60,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 100004,
        group_name: 'Labs',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: true,
        excused: false,
        omit: false,
      },
      {
        id: 1006,
        name: 'Taylor Series Exploration',
        description: '<p>Explore Taylor and Maclaurin series representations of common functions. Include convergence analysis.</p>',
        due_at: daysFromNow(10),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 100002,
        group_name: 'Homework',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 100001, name: 'Quizzes', weight: 20, rules: {}, group_points: 50, submission_points: 48, percentage: 96.0 },
      { id: 100002, name: 'Homework', weight: 30, rules: {}, group_points: 275, submission_points: 95, percentage: 95.0 },
      { id: 100003, name: 'Exams', weight: 40, rules: {}, group_points: 150, submission_points: 142, percentage: 94.67 },
      { id: 100004, name: 'Labs', weight: 10, rules: {}, group_points: 60, submission_points: 0, percentage: 0.0 },
    ],
  },
  461002: {
    id: '461002',
    name: 'P3-AP Physics C Mechanics-CHEN',
    course_code: 'P3-AP Physics C Mechanics-CHEN',
    overall_grade: 87.8,
    assignments: [
      {
        id: 2001,
        name: 'Rotational Motion Lab Report',
        description: '<p>Lab report on moment of inertia and angular acceleration experiments. Include data tables, graphs, and error analysis.</p>',
        due_at: daysAgo(10),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 200001,
        group_name: 'Lab Reports',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(10),
        score: 88,
        grade: '88',
        graded_at: daysAgo(7),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 2002,
        name: 'Energy Conservation Problem Set',
        description: '<p>Problems involving work-energy theorem and conservation of mechanical energy. Show all derivations.</p>',
        due_at: daysAgo(5),
        points_possible: 80,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 200002,
        group_name: 'Problem Sets',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(5),
        score: 72,
        grade: '72',
        graded_at: daysAgo(3),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 2003,
        name: 'Momentum and Collisions Quiz',
        description: '<p>Quiz on linear momentum, impulse, and elastic/inelastic collisions. 30 minutes, closed book.</p>',
        due_at: daysFromNow(5),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 200003,
        group_name: 'Quizzes',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 2004,
        name: 'Simple Harmonic Motion Lab',
        description: '<p>Investigate oscillations with springs and pendulums. Measure period vs amplitude and mass relationships.</p>',
        due_at: daysFromNow(12),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 200001,
        group_name: 'Lab Reports',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 2005,
        name: 'Midterm Exam',
        description: '<p>Comprehensive exam covering kinematics through rotational dynamics. Two hours, formula sheet provided.</p>',
        due_at: daysFromNow(14),
        points_possible: 200,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 200004,
        group_name: 'Exams',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 200001, name: 'Lab Reports', weight: 25, rules: {}, group_points: 200, submission_points: 88, percentage: 88.0 },
      { id: 200002, name: 'Problem Sets', weight: 35, rules: {}, group_points: 80, submission_points: 72, percentage: 90.0 },
      { id: 200003, name: 'Quizzes', weight: 15, rules: {}, group_points: 50, submission_points: 0, percentage: 0.0 },
      { id: 200004, name: 'Exams', weight: 25, rules: {}, group_points: 200, submission_points: 0, percentage: 0.0 },
    ],
  },
  461003: {
    id: '461003',
    name: 'P4-AP English Lit-MARTINEZ',
    course_code: 'P4-AP English Lit-MARTINEZ',
    overall_grade: 91.5,
    assignments: [
      {
        id: 3001,
        name: 'Hamlet Character Analysis Essay',
        description: '<p>Analyze the psychological complexity of a major character in Hamlet. 5-7 pages, MLA format. Use at least 3 scholarly sources.</p>',
        due_at: daysAgo(12),
        points_possible: 200,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 300001,
        group_name: 'Essays',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(12),
        score: 185,
        grade: '185',
        graded_at: daysAgo(8),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 3002,
        name: 'Poetry Analysis: Metaphysical Poets',
        description: '<p>Close reading analysis of a poem by Donne, Herbert, or Marvell. Focus on conceits, paradox, and wit.</p>',
        due_at: daysAgo(5),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 300002,
        group_name: 'Analysis',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(5),
        score: 92,
        grade: '92',
        graded_at: daysAgo(2),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 3003,
        name: 'Reading Response: Paradise Lost Books 1-4',
        description: '<p>Weekly reading response on Milton\'s epic. Address themes of free will, temptation, and the nature of evil.</p>',
        due_at: daysFromNow(2),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['online_text_entry'],
        group_id: 300003,
        group_name: 'Reading Responses',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 3004,
        name: 'Comparative Essay: Epic Traditions',
        description: '<p>Compare Paradise Lost with classical epics (Iliad, Aeneid, or Divine Comedy). 8-10 pages, scholarly sources required.</p>',
        due_at: daysFromNow(18),
        points_possible: 250,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 300001,
        group_name: 'Essays',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 3005,
        name: 'Class Participation - Week 8',
        description: '<p>Participation grade for seminar discussions.</p>',
        due_at: daysAgo(1),
        points_possible: 25,
        omit_from_final_grade: false,
        submission_types: ['none'],
        group_id: 300004,
        group_name: 'Participation',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: 23,
        grade: '23',
        graded_at: daysAgo(1),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 300001, name: 'Essays', weight: 50, rules: {}, group_points: 450, submission_points: 185, percentage: 92.5 },
      { id: 300002, name: 'Analysis', weight: 30, rules: {}, group_points: 100, submission_points: 92, percentage: 92.0 },
      { id: 300003, name: 'Reading Responses', weight: 10, rules: {}, group_points: 50, submission_points: 0, percentage: 0.0 },
      { id: 300004, name: 'Participation', weight: 10, rules: {}, group_points: 25, submission_points: 23, percentage: 92.0 },
    ],
  },
  461004: {
    id: '461004',
    name: 'P5-AP US History-WILLIAMS',
    course_code: 'P5-AP US History-WILLIAMS',
    overall_grade: 89.3,
    assignments: [
      {
        id: 4001,
        name: 'Civil War Reconstruction Research Paper',
        description: '<p>Research paper on an aspect of Reconstruction era (1865-1877). Use primary sources and historiographical analysis.</p>',
        due_at: daysAgo(8),
        points_possible: 150,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 400001,
        group_name: 'Research Papers',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(8),
        score: 138,
        grade: '138',
        graded_at: daysAgo(4),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 4002,
        name: 'Chapter 12 Quiz: The Gilded Age',
        description: '<p>Quiz on industrialization, immigration, and urbanization. Multiple choice and short answer.</p>',
        due_at: daysFromNow(1),
        points_possible: 25,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 400002,
        group_name: 'Quizzes',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 4003,
        name: 'Primary Source Analysis: Progressive Era',
        description: '<p>Analyze primary sources from muckrakers and reformers. Compare perspectives of Sinclair, Tarbell, and Riis.</p>',
        due_at: daysFromNow(8),
        points_possible: 75,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 400003,
        group_name: 'Source Analysis',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 4004,
        name: 'Discussion Post: WWI Causes',
        description: '<p>Discuss factors leading to American entry into World War I. Respond to at least two classmates.</p>',
        due_at: daysAgo(1),
        points_possible: 20,
        omit_from_final_grade: false,
        submission_types: ['discussion_topic'],
        group_id: 400004,
        group_name: 'Discussions',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: true,
        excused: false,
        omit: false,
      },
      {
        id: 4005,
        name: 'DBQ Practice: Imperialism',
        description: '<p>Document-based question practice on American imperialism. 45-minute timed write.</p>',
        due_at: daysAgo(15),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 400001,
        group_name: 'Research Papers',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(15),
        score: 87,
        grade: '87',
        graded_at: daysAgo(12),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 400001, name: 'Research Papers', weight: 40, rules: {}, group_points: 250, submission_points: 225, percentage: 90.0 },
      { id: 400002, name: 'Quizzes', weight: 20, rules: {}, group_points: 25, submission_points: 0, percentage: 0.0 },
      { id: 400003, name: 'Source Analysis', weight: 25, rules: {}, group_points: 75, submission_points: 0, percentage: 0.0 },
      { id: 400004, name: 'Discussions', weight: 15, rules: {}, group_points: 20, submission_points: 0, percentage: 0.0 },
    ],
  },
  461005: {
    id: '461005',
    name: 'P6-Spanish III-GARCIA',
    course_code: 'P6-Spanish III-GARCIA',
    overall_grade: 96.1,
    assignments: [
      {
        id: 5001,
        name: 'Subjunctive Mood Practice',
        description: '<p>Practice exercises using the subjunctive in various contexts: wishes, emotions, doubt, and impersonal expressions.</p>',
        due_at: daysAgo(6),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 500001,
        group_name: 'Practice',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(6),
        score: 48,
        grade: '48',
        graded_at: daysAgo(4),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 5002,
        name: 'Oral Presentation: Mi Ciudad',
        description: '<p>3-5 minute oral presentation about your city or hometown. Include cultural aspects and personal connections.</p>',
        due_at: daysAgo(3),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['media_recording'],
        group_id: 500002,
        group_name: 'Speaking',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(3),
        score: 97,
        grade: '97',
        graded_at: daysAgo(1),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 5003,
        name: 'Chapter 8 Vocabulary Quiz',
        description: '<p>Vocabulary quiz on travel and transportation. 40 words, matching and fill-in-the-blank.</p>',
        due_at: daysFromNow(4),
        points_possible: 30,
        omit_from_final_grade: false,
        submission_types: ['online_quiz'],
        group_id: 500003,
        group_name: 'Quizzes',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 5004,
        name: 'Listening Comprehension: Podcast Analysis',
        description: '<p>Listen to assigned Spanish podcast episode and answer comprehension questions.</p>',
        due_at: daysAgo(10),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['online_text_entry'],
        group_id: 500004,
        group_name: 'Listening',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(10),
        score: 48,
        grade: '48',
        graded_at: daysAgo(8),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 5005,
        name: 'Written Composition: Un Viaje Memorable',
        description: '<p>Write about a memorable trip (real or imagined) using past tenses. 300-400 words.</p>',
        due_at: daysFromNow(7),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 500005,
        group_name: 'Writing',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 500001, name: 'Practice', weight: 15, rules: {}, group_points: 50, submission_points: 48, percentage: 96.0 },
      { id: 500002, name: 'Speaking', weight: 25, rules: {}, group_points: 100, submission_points: 97, percentage: 97.0 },
      { id: 500003, name: 'Quizzes', weight: 20, rules: {}, group_points: 30, submission_points: 0, percentage: 0.0 },
      { id: 500004, name: 'Listening', weight: 15, rules: {}, group_points: 50, submission_points: 48, percentage: 96.0 },
      { id: 500005, name: 'Writing', weight: 25, rules: {}, group_points: 100, submission_points: 0, percentage: 0.0 },
    ],
  },
  461006: {
    id: '461006',
    name: 'P8-AP Comp Sci Prin-THOMPSON',
    course_code: 'P8-AP Comp Sci Prin-THOMPSON',
    overall_grade: 63.9,
    assignments: [
      {
        id: 6001,
        name: 'Python Functions Lab',
        description: '<p>Create functions to solve various computational problems. Include docstrings and test cases.</p>',
        due_at: daysAgo(7),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 600001,
        group_name: 'Labs',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(8),
        score: 100,
        grade: '100',
        graded_at: daysAgo(5),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6002,
        name: 'Data Structures Quiz',
        description: '<p>Quiz on lists, dictionaries, and basic algorithms. Code tracing and output prediction.</p>',
        due_at: daysAgo(4),
        points_possible: 50,
        omit_from_final_grade: false,
        submission_types: ['on_paper'],
        group_id: 600002,
        group_name: 'Quizzes',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: 48,
        grade: '48',
        graded_at: daysAgo(2),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6003,
        name: 'Create Task: App Development',
        description: '<p>AP CSP Create Task - develop a program that demonstrates algorithms, abstraction, and program design.</p>',
        due_at: daysFromNow(21),
        points_possible: 200,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 600003,
        group_name: 'Projects',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: false,
        excused: null,
        omit: false,
      },
      {
        id: 6004,
        name: 'Unit 1: Internet and Cybersecurity Test',
        description: '<p>Test on internet protocols, cybersecurity concepts, and digital citizenship.</p>',
        due_at: daysAgo(30),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_text_entry'],
        group_id: 600004,
        group_name: 'Majors',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: 76,
        grade: '76',
        graded_at: daysAgo(28),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6005,
        name: 'Python Test - Booleans, Ifs, While loops',
        description: '<p>Test on boolean logic, conditional statements, and while loops.</p>',
        due_at: daysAgo(20),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['on_paper'],
        group_id: 600004,
        group_name: 'Majors',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: 66,
        grade: '66',
        graded_at: daysAgo(17),
        late: false,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6006,
        name: 'Add to Cart Programming Assignment',
        description: '<p>Build an online ordering system with variables, conditionals, and user input. Must handle edge cases.</p>',
        due_at: daysAgo(25),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['media_recording', 'online_upload'],
        group_id: 600005,
        group_name: 'Minors',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(24),
        score: 80,
        grade: '80',
        graded_at: daysAgo(20),
        late: true,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6007,
        name: 'Choose Your Own Adventure',
        description: '<p>Design and code an interactive Choose-Your-Own-Adventure game with branching storylines and while loops.</p>',
        due_at: daysAgo(14),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_text_entry', 'online_upload'],
        group_id: 600004,
        group_name: 'Majors',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: daysAgo(8),
        score: 65,
        grade: '65',
        graded_at: daysAgo(7),
        late: true,
        missing: false,
        excused: false,
        omit: false,
      },
      {
        id: 6008,
        name: 'Computing Innovations PSA/Infographic',
        description: '<p>Research a computing innovation and create a PSA or infographic about its impacts.</p>',
        due_at: daysAgo(18),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 600006,
        group_name: 'Practice',
        locked_for_user: true,
        lock_at: daysAgo(10),
        unlock_at: daysAgo(20),
        lock_info: { lock_at: daysAgo(10), can_view: true, asset_string: 'assignment_6008' },
        lock_explanation: 'This assignment was locked.',
        submitted_at: null,
        score: 0,
        grade: '0',
        graded_at: daysAgo(18),
        late: false,
        missing: true,
        excused: null,
        omit: false,
      },
      {
        id: 6009,
        name: 'String Manipulation Assignment',
        description: '<p>Complete string manipulation exercises using Python string methods and slicing.</p>',
        due_at: daysAgo(12),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['online_upload'],
        group_id: 600006,
        group_name: 'Practice',
        locked_for_user: false,
        lock_at: daysFromNow(30),
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: 0,
        grade: '0',
        graded_at: daysAgo(10),
        late: false,
        missing: true,
        excused: false,
        omit: false,
      },
      {
        id: 6010,
        name: 'Spotify Playlist Project',
        description: '<p>Create a playlist management program using lists and dictionaries.</p>',
        due_at: daysFromNow(0),
        points_possible: 100,
        omit_from_final_grade: false,
        submission_types: ['media_recording', 'online_upload'],
        group_id: 600005,
        group_name: 'Minors',
        locked_for_user: false,
        lock_at: null,
        unlock_at: null,
        lock_info: {},
        lock_explanation: null,
        submitted_at: null,
        score: null,
        grade: null,
        graded_at: null,
        late: false,
        missing: true,
        excused: false,
        omit: false,
      },
    ],
    assignment_groups: [
      { id: 600001, name: 'Labs', weight: 0, rules: {}, group_points: 100, submission_points: 100, percentage: 100.0 },
      { id: 600002, name: 'Quizzes', weight: 0, rules: {}, group_points: 50, submission_points: 48, percentage: 96.0 },
      { id: 600003, name: 'Projects', weight: 0, rules: {}, group_points: 200, submission_points: 0, percentage: 0.0 },
      { id: 600004, name: 'Majors', weight: 55, rules: {}, group_points: 400, submission_points: 207, percentage: 51.75 },
      { id: 600005, name: 'Minors', weight: 35, rules: {}, group_points: 200, submission_points: 80, percentage: 40.0 },
      { id: 600006, name: 'Practice', weight: 10, rules: {}, group_points: 200, submission_points: 0, percentage: 0.0 },
    ],
  },
};

// Grade history for trend charts (showing grade progression over time)
export const MOCK_COURSE_HISTORY = {
  461001: [
    { date: daysAgo(28), score: 91.0 },
    { date: daysAgo(21), score: 92.5 },
    { date: daysAgo(14), score: 93.8 },
    { date: daysAgo(7), score: 94.0 },
    { date: daysAgo(3), score: 94.2 },
  ],
  461002: [
    { date: daysAgo(28), score: 84.0 },
    { date: daysAgo(21), score: 85.5 },
    { date: daysAgo(14), score: 87.2 },
    { date: daysAgo(7), score: 88.5 },
    { date: daysAgo(5), score: 87.8 },
  ],
  461003: [
    { date: daysAgo(28), score: 88.0 },
    { date: daysAgo(21), score: 89.5 },
    { date: daysAgo(14), score: 90.2 },
    { date: daysAgo(5), score: 91.5 },
  ],
  461004: [
    { date: daysAgo(28), score: 86.0 },
    { date: daysAgo(21), score: 87.5 },
    { date: daysAgo(14), score: 88.2 },
    { date: daysAgo(8), score: 89.3 },
  ],
  461005: [
    { date: daysAgo(28), score: 94.0 },
    { date: daysAgo(21), score: 95.0 },
    { date: daysAgo(14), score: 95.5 },
    { date: daysAgo(6), score: 96.1 },
  ],
  461006: [
    { date: daysAgo(28), score: 97.0 },
    { date: daysAgo(21), score: 97.5 },
    { date: daysAgo(14), score: 98.0 },
    { date: daysAgo(4), score: 98.5 },
  ],
};

// Demo mode helpers
export const DEMO_MODE_KEY = 'isDemoMode';

/**
 * Check if the app is currently in demo mode
 */
export const isDemoMode = () => {
  return sessionStorage.getItem(DEMO_MODE_KEY) === 'true';
};

/**
 * Enable or disable demo mode
 */
export const setDemoMode = (enabled) => {
  if (enabled) {
    sessionStorage.setItem(DEMO_MODE_KEY, 'true');
  } else {
    sessionStorage.removeItem(DEMO_MODE_KEY);
  }
};

/**
 * Clear all demo mode data from session storage
 */
export const clearDemoMode = () => {
  sessionStorage.removeItem(DEMO_MODE_KEY);
};

/**
 * Simulate API delay to make demo feel realistic
 * @param {number} minMs - Minimum delay in milliseconds
 * @param {number} maxMs - Maximum delay in milliseconds
 */
export const simulateApiDelay = (minMs = 200, maxMs = 600) => {
  const delay = Math.random() * (maxMs - minMs) + minMs;
  return new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Get a summary of demo data for logging/debugging
 */
export const getDemoDataSummary = () => ({
  courses: MOCK_COURSES.length,
  totalAssignments: Object.values(MOCK_COURSE_DETAILS).reduce(
    (sum, course) => sum + (course.assignments?.length || 0),
    0
  ),
  missingAssignments: Object.values(MOCK_COURSE_DETAILS).reduce(
    (sum, course) => sum + (course.assignments?.filter(a => {
      const isPastDue = new Date(a.due_at) < new Date();
      const notSubmitted = !a.submission;
      return isPastDue && notSubmitted;
    }).length || 0),
    0
  ),
  upcomingAssignments: Object.values(MOCK_COURSE_DETAILS).reduce(
    (sum, course) => sum + (course.assignments?.filter(a => {
      const isFuture = new Date(a.due_at) > new Date();
      const notSubmitted = !a.submission;
      return isFuture && notSubmitted;
    }).length || 0),
    0
  ),
});

export default {
  MOCK_USER,
  MOCK_COURSES,
  MOCK_COURSE_DETAILS,
  MOCK_COURSE_HISTORY,
  isDemoMode,
  setDemoMode,
  clearDemoMode,
  simulateApiDelay,
  getDemoDataSummary,
};
